// Phase 23C — Outlet Density Map (managers + admin)
// OSM/Leaflet + react-leaflet-cluster. Reuses the Leaflet setup from
// LocationMap.jsx (attribution, default icons, tile URL).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, MapPin, Store, Building2, EyeOff, ChevronDown, ChevronRight,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import PageHeader from "@/components/PageHeader";

// Fix Leaflet default icons under webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Distributor HQ pin — orange square, distinct from outlet blue pins
const distributorIcon = L.divIcon({
  html: `<div style="background:#ea580c;width:16px;height:16px;border:2px solid #fff;border-radius:3px;box-shadow:0 0 4px rgba(0,0,0,.5)"></div>`,
  className: "distributor-hq-icon",
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

// Channel-type -> color, so outlet type is identifiable on the map at a
// glance instead of every pin being an identical blue dot. Falls back to
// the neutral color for "unspecified"/unknown channels.
const CHANNEL_COLORS = {
  grocery: "#059669", pharmacy: "#0891b2", general: "#2563eb",
  wholesale: "#7c3aed", horeca: "#d97706", supermarket: "#db2777",
  unspecified: "#64748b",
};
function channelColor(channelType) {
  return CHANNEL_COLORS[(channelType || "unspecified").toLowerCase()] || CHANNEL_COLORS.unspecified;
}
function outletIcon(channelType, highlighted) {
  const color = channelColor(channelType);
  const size = highlighted ? 22 : 14;
  return L.divIcon({
    html: `<div style="background:${color};width:${size}px;height:${size}px;
            border:2px solid #fff;border-radius:50%;box-shadow:0 0 ${highlighted ? 8 : 3}px rgba(0,0,0,.5)
            ${highlighted ? ";outline:2px solid " + color + ";outline-offset:1px" : ""}"></div>`,
    className: "outlet-icon",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
const retailerDetailsUrl = (id) => `/field/retailer/${id}/dashboard`;

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

export default function OutletDensityMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDistributors, setShowDistributors] = useState(false);
  // The retailer picked from a suggestion — its pin renders bigger/glowing
  // so it's obvious which one you searched for once the map zooms in.
  const [selectedRetailerId, setSelectedRetailerId] = useState(null);
  const selectedMarkerRef = useRef(null);
  const [notPlottedOpen, setNotPlottedOpen] = useState(false);

  // Zone / Sub-zone / Distributor via the shared filter bar; Beat is a
  // separate picker below (CommonFilterBar has no beat option) narrowed by
  // whichever of zone/distributor is already picked; retailer search (code,
  // name, owner or phone) is its own free-text box — with 65k+ outlets on
  // one map, finding a specific one by panning/zooming isn't realistic.
  const [filters, setFilters] = useState({ zone_id: "", sub_zone_id: "", distributor_id: "" });
  const [beatId, setBeatId] = useState("");
  const [beats, setBeats] = useState([]);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  // Type-ahead suggestions (code/name/owner/phone matches) from the
  // existing /retailers search — a small, fast list to click straight to
  // one outlet, instead of only narrowing the (still possibly huge)
  // density-map result set after a full debounce.
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);

  useEffect(() => {
    api.get("/beats", { params: { page_size: 2000 } })
       .then(r => setBeats(r.data.items || []))
       .catch(() => setBeats([]));
  }, []);

  // Debounce the free-text search so every keystroke doesn't refetch 65k rows.
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  // Debounced suggestion fetch — same text, shorter/faster query against
  // /retailers (page_size 8) rather than the heavy density-map aggregate.
  useEffect(() => {
    const term = qInput.trim();
    if (term.length < 2) { setSuggestions([]); setSuggestLoading(false); return; }
    setSuggestLoading(true);
    const t = setTimeout(() => {
      api.get("/retailers", {
        params: {
          q: term, page_size: 8,
          zone_id: filters.zone_id || undefined,
          distributor_id: filters.distributor_id || undefined,
          beat_id: beatId || undefined,
        },
      }).then(r => setSuggestions(r.data.items || []))
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [qInput, filters.zone_id, filters.distributor_id, beatId]);

  // Clearing the search box drops the highlight too, rather than leaving a
  // glowing pin for a search that's no longer active.
  useEffect(() => {
    if (!qInput.trim()) setSelectedRetailerId(null);
  }, [qInput]);

  const beatOptions = useMemo(() => {
    let list = beats;
    if (filters.distributor_id) list = list.filter(b => b.distributor_id === filters.distributor_id);
    else if (filters.zone_id) list = list.filter(b => b.zone_id === filters.zone_id);
    return list.map(b => ({ id: b.id, code: b.code, name: b.name }));
  }, [beats, filters.distributor_id, filters.zone_id]);

  const load = useCallback(async (withHQ) => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/outlets/density-map", {
        params: {
          include_distributor_hq: withHQ ? "true" : "false",
          zone_id: filters.zone_id || undefined,
          sub_zone_id: filters.sub_zone_id || undefined,
          distributor_id: filters.distributor_id || undefined,
          beat_id: beatId || undefined,
          q: q || undefined,
        },
      });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); setData(null); }
    finally { setLoading(false); }
  }, [filters.zone_id, filters.sub_zone_id, filters.distributor_id, beatId, q]);

  useEffect(() => { load(showDistributors); }, [load, showDistributors]);

  // A distributor/zone change can invalidate the currently-picked beat —
  // clear it rather than silently keep filtering on a beat that's no
  // longer even shown as an option.
  useEffect(() => {
    if (beatId && !beatOptions.some(b => b.id === beatId)) setBeatId("");
  }, [beatOptions, beatId]);

  const stats = data?.stats || { total: 0, plotted: 0, not_plotted: 0, by_type: {} };
  const plotted = data?.plotted || [];
  const notPlotted = data?.not_plotted || [];
  const distributors = data?.distributor_hq || [];

  // Once the search result (re)loads, pop open the highlighted marker's
  // balloon so its details are visible immediately, not just its glow.
  useEffect(() => {
    if (!selectedRetailerId) return;
    const t = setTimeout(() => {
      try { selectedMarkerRef.current?.openPopup(); } catch { /* marker not mounted yet — glow alone still identifies it */ }
    }, 300);
    return () => clearTimeout(t);
  }, [selectedRetailerId, data]);

  const points = useMemo(() => plotted.map(p => [p.lat, p.lng]), [plotted]);

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="outlet-density-map-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "Outlet density map" }]}
        title="Outlet density map"
        subtitle="Heat-map of retailer outlets by zone and territory."
        favorite={{ key: "admin.outlet-density-map", type: "screen", label: "Outlet density map", route: "/admin/outlet-density-map" }}
        testid="outletdensitymap-page-header"
      />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-[#6D2C91]" /> Outlet Density Map
          </h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Marker clusters group nearby outlets. Zoom in to see individual pins.
            Visibility respects your zone / hierarchy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border px-3 py-2 bg-white">
            <Switch id="show-distributors" checked={showDistributors}
                    onCheckedChange={setShowDistributors}
                    data-testid="toggle-distributor-hq" />
            <Label htmlFor="show-distributors" className="text-sm cursor-pointer">
              Show distributor HQs
            </Label>
          </div>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
        </div>
      </div>

      {/* Find outlets — zone / sub-zone / distributor / beat / retailer text search */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3 flex flex-wrap items-end gap-3">
          <CommonFilterBar value={filters} onChange={setFilters}
                           showZone showSubZone showDistributor />
          <div className="w-[47%] min-w-[140px] flex-1">
            <Label className="text-[11px]">Beat</Label>
            <SearchableSelect
              value={beatId}
              onChange={setBeatId}
              options={beatOptions}
              placeholder="All beats"
              testid="density-filter-beat"
              className="h-9"
            />
          </div>
          <div className="w-[47%] min-w-[200px] flex-1 relative">
            <Label className="text-[11px]">Retailer (code / name / owner / phone)</Label>
            <Input placeholder="Search retailer…" value={qInput}
                   onChange={(e) => setQInput(e.target.value)}
                   onFocus={() => setSuggestOpen(true)}
                   onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                   className="h-9" data-testid="density-filter-retailer-q" />
            {suggestOpen && qInput.trim().length >= 2 && (
              <div className="absolute z-[1000] mt-1 w-full max-h-64 overflow-y-auto rounded-lg
                              border border-neutral-200 bg-white shadow-lg"
                   data-testid="density-retailer-suggestions">
                {suggestLoading && (
                  <div className="px-3 py-2 text-xs text-neutral-400 flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
                  </div>
                )}
                {!suggestLoading && suggestions.length === 0 && (
                  <div className="px-3 py-2 text-xs text-neutral-400">No retailers match.</div>
                )}
                {!suggestLoading && suggestions.map((r) => (
                  <div key={r.id}
                       className="flex items-center gap-2 px-2.5 py-1.5 border-b last:border-b-0
                                  border-neutral-100 hover:bg-neutral-50 group"
                       data-testid={`density-retailer-suggest-${r.code}`}>
                    <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                          style={{ background: channelColor(r.channel_type) }} />
                    <button type="button"
                            title="Locate on map"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setQInput(r.code || r.name || "");
                              setQ(r.code || r.name || "");
                              setSelectedRetailerId(r.id);
                              setSuggestOpen(false);
                            }}
                            className="flex-1 min-w-0 text-left">
                      <div className="font-semibold text-neutral-900 text-xs truncate">
                        {r.code} · {r.name}
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {[r.distributor_code, r.beat_code].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </button>
                    <a href={retailerDetailsUrl(r.id)} target="_blank" rel="noreferrer"
                       title="View retailer details"
                       onMouseDown={(e) => e.stopPropagation()}
                       className="shrink-0 text-[11px] font-semibold text-[#6D2C91]
                                  underline opacity-0 group-hover:opacity-100 transition-opacity"
                       data-testid={`density-retailer-suggest-details-${r.code}`}>
                      Details ↗
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Header stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="density-stats">
        <StatTile icon={<Store className="h-4 w-4" />} label="Total outlets"
                  value={stats.total} tone="neutral" />
        <StatTile icon={<MapPin className="h-4 w-4 text-emerald-600" />}
                  label="Plotted (GPS)" value={stats.plotted} tone="emerald"
                  testid="stat-plotted" />
        <StatTile icon={<EyeOff className="h-4 w-4 text-amber-600" />}
                  label="Not plotted" value={stats.not_plotted} tone="amber"
                  testid="stat-not-plotted" />
        <StatTile icon={<Building2 className="h-4 w-4 text-orange-600" />}
                  label="Distributor HQs"
                  value={showDistributors ? stats.distributor_hq_count : "—"}
                  tone="orange" />
      </div>

      {/* Type breakdown */}
      {Object.keys(stats.by_type || {}).length > 0 && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="p-3 flex flex-wrap gap-2" data-testid="density-type-breakdown">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-2 self-center">
              By channel
            </span>
            {Object.entries(stats.by_type).map(([code, count]) => (
              <Badge key={code} variant="outline"
                     className="text-xs bg-neutral-50 border-neutral-200 text-neutral-700 capitalize
                                flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: channelColor(code === "unspecified" ? null : code) }} />
                {code.replace(/_/g, " ")} · {count}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Map */}
      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0" style={{ height: "560px" }}
                     data-testid="density-map-canvas">
          <MapContainer center={[23.0225, 72.5714]} zoom={7}
                          style={{ height: "100%", width: "100%" }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />

            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={80}
              spiderfyOnMaxZoom
              showCoverageOnHover={false}
            >
              {plotted.map(p => (
                <Marker key={p.id} position={[p.lat, p.lng]}
                        icon={outletIcon(p.channel_type, p.id === selectedRetailerId)}
                        ref={p.id === selectedRetailerId ? selectedMarkerRef : undefined}>
                  <Popup>
                    <div className="text-xs min-w-[180px]">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ background: channelColor(p.channel_type) }} />
                        <div className="font-semibold text-sm text-neutral-900">{p.name}</div>
                      </div>
                      <div className="text-neutral-500 font-mono mt-0.5">{p.code}</div>
                      {p.channel_type && (
                        <div className="mt-1 capitalize text-neutral-600">
                          {p.channel_type.replace(/_/g, " ")}
                        </div>
                      )}
                      {p.owner_name && (
                        <div className="mt-1 text-neutral-600">👤 {p.owner_name}</div>
                      )}
                      {p.phone && (
                        <div className="text-neutral-600">📞 {p.phone}</div>
                      )}
                      {p.distributor_label && (
                        <div className="mt-1 text-neutral-500">Dist: {p.distributor_label}</div>
                      )}
                      {p.beat_label && (
                        <div className="text-neutral-500">Beat: {p.beat_label}</div>
                      )}
                      <a href={retailerDetailsUrl(p.id)} target="_blank" rel="noreferrer"
                         className="mt-2 inline-block text-[#6D2C91] font-semibold underline"
                         data-testid={`density-marker-details-${p.code}`}>
                        View details ↗
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MarkerClusterGroup>

            {showDistributors && distributors.map(d => (
              <Marker key={d.id} position={[d.lat, d.lng]} icon={distributorIcon}>
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold text-sm">{d.name}</div>
                    <div className="text-neutral-500 font-mono">{d.code}</div>
                    <div className="mt-1 text-orange-600 font-medium">Distributor HQ</div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {points.length > 0 && <FitBounds points={points} />}
          </MapContainer>
        </CardContent>
      </Card>

      {/* Not plotted expandable list */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => setNotPlottedOpen(v => !v)}
            className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition rounded-2xl"
            data-testid="toggle-not-plotted"
          >
            <div className="flex items-center gap-2">
              {notPlottedOpen
                ? <ChevronDown className="h-4 w-4 text-neutral-400" />
                : <ChevronRight className="h-4 w-4 text-neutral-400" />}
              <EyeOff className="h-4 w-4 text-amber-600" />
              <span className="font-medium text-sm">
                Outlets without GPS
              </span>
              <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800">
                {stats.not_plotted}
              </Badge>
            </div>
            <span className="text-xs text-neutral-400">
              {notPlottedOpen ? "Hide" : "Expand"}
            </span>
          </button>
          {notPlottedOpen && (
            <div className="border-t max-h-[360px] overflow-y-auto"
                 data-testid="not-plotted-list">
              {notPlotted.length === 0 ? (
                <div className="p-6 text-center text-sm text-neutral-500">
                  All in-scope outlets have GPS. Nothing to plot here.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-left text-neutral-600">
                    <tr>
                      <th className="px-4 py-2 font-medium text-xs">Code</th>
                      <th className="px-4 py-2 font-medium text-xs">Name</th>
                      <th className="px-4 py-2 font-medium text-xs">Channel</th>
                      <th className="px-4 py-2 font-medium text-xs">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notPlotted.map(o => (
                      <tr key={o.id} className="border-t hover:bg-neutral-50"
                          data-testid={`not-plotted-row-${o.code}`}>
                        <td className="px-4 py-2 font-mono text-xs">{o.code}</td>
                        <td className="px-4 py-2">{o.name}</td>
                        <td className="px-4 py-2 capitalize text-neutral-500 text-xs">
                          {(o.channel_type || "—").replace(/_/g, " ")}
                        </td>
                        <td className="px-4 py-2">
                          <Button size="sm" variant="ghost" asChild>
                            <a href={`/admin/outlets/${o.id}`}>Open</a>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function StatTile({ icon, label, value, tone, testid }) {
  const toneCls = {
    neutral: "text-neutral-900",
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    orange:  "text-orange-700",
  }[tone] || "text-neutral-900";
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid={testid}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-neutral-500">
          {icon}{label}
        </div>
        <div className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
